import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid,
  Paper,
  Tooltip,
  Typography,
  useTheme
} from '@material-ui/core'
import { Cancel, CheckCircle, Info } from '@material-ui/icons'
import { Snackbar } from 'elentari'
import React, { useEffect, useState } from 'react'
import gsvtechRepository from './gsvtechRepository'

const isBloqueado = bloqueado => {
  if (bloqueado == null || bloqueado === '') return false
  return String(bloqueado).trim().toLowerCase() === 'sim'
}

const CardGSVTech = ({ solicitacaoId }) => {
  const [initialLoading, setInitialLoading] = useState(true)
  const [loading, setLoading] = useState(false)
  const [gsvtech, setGSVTech] = useState(null)
  const [message, setMessage] = useState('')
  const theme = useTheme()

  useEffect(() => {
    const getData = async () => {
      setInitialLoading(true)
      const result = await gsvtechRepository.fetch(solicitacaoId)

      if (!result.ok) {
        setMessage(result.data.message)
      }

      setGSVTech(result.data)
      setInitialLoading(false)
    }

    getData()
  }, [])

  const handleRecreate = async () => {
    setLoading(true)
    const result = await gsvtechRepository.recreate(solicitacaoId)

    if (!result.ok) {
      setMessage(result.data.message)
    }

    setGSVTech(result.data)
  }

  const handleRefresh = async () => {
    setLoading(true)
    const result = await gsvtechRepository.refreshResult(solicitacaoId)

    if (!result.ok) {
      setMessage(result.data.message)
    }

    setGSVTech(result.data)
    setLoading(false)
  }

  const renderEstadoConsulta = () => {
    if (gsvtech?.status === 'ERRO') {
      return <GSVTechErro handleRecreate={handleRecreate} loading={loading} />
    }

    if (gsvtech?.status === 'CONSULTANDO') {
      return (
        <GSVTechConsultando handleRefresh={handleRefresh} loading={loading} />
      )
    }

    if (gsvtech?.status === 'CONCLUIDO') {
      return (
        <CheckCircle
          style={{
            width: 20,
            height: 20,
            color: theme.palette.success.dark
          }}
          color="inherit"
        />
      )
    }

    return null
  }

  return (
    <>
      <Grid container spacing={1} alignItems="center">
        <Grid container item xs={12} alignItems="center">
          <Grid item>
            <Typography
              variant="h6"
              display="inline"
              style={{ marginRight: 4 }}
            >
              Consulta GSVTech
            </Typography>
          </Grid>
          <Grid item>
            {initialLoading ? (
              <CircularProgress size={24} />
            ) : (
              renderEstadoConsulta()
            )}
          </Grid>
        </Grid>

        <Grid item xs={12}>
          {!initialLoading ? <RenderCards gsvtech={gsvtech} /> : null}
        </Grid>
      </Grid>

      <Snackbar
        onClose={() => {
          setMessage('')
        }}
        autoHideDuration={2000}
        message={message}
      />
    </>
  )
}

const RenderCards = ({ gsvtech }) => {
  const theme = useTheme()

  if (gsvtech.status !== 'CONCLUIDO') {
    return null
  }

  if (gsvtech.data.length === 0) {
    return (
      <Typography variant="h6" align="center">
        Nenhum Histórico de Viagem!
      </Typography>
    )
  }

  const renderStatus = bloqueado => {
    const style = bloqueado
      ? {
          backgroundColor: theme.palette.error.dark,
          color: theme.palette.getContrastText(theme.palette.error.dark)
        }
      : {
          backgroundColor: theme.palette.success.dark,
          color: theme.palette.getContrastText(theme.palette.success.dark)
        }
    const label = bloqueado ? 'Bloqueado' : 'Ativo'

    return <Chip size="small" label={label} style={style} />
  }

  return (
    <Grid container spacing={4}>
      {gsvtech.data
        .sort((a, b) => new Date(b.data).valueOf() - new Date(a.data).valueOf())
        .map((data, index) => (
          <Grid item xs={12} sm={3} key={index}>
            <Paper
              style={{
                padding: 8,
                minHeight: '260px'
              }}
            >
              <Typography variant="body1" noWrap>
                <b>{data.origem}</b>
              </Typography>

              <Box p={1}>
                <Typography variant="caption" display="block">
                  Quantidade: {data.quantidade}
                </Typography>

                <Typography variant="body2" display="block">
                  <b>Última Viagem</b>
                </Typography>
              </Box>

              <Box p={1}>
                <Typography variant="caption" display="block">
                  Status Motorista:{' '}
                  {renderStatus(isBloqueado(data.bloqueado))}
                </Typography>
                <Typography variant="caption" display="block">
                  Data: {data?.data}
                </Typography>
                <Typography variant="caption" display="block">
                  Produto: {data?.produto}
                </Typography>
                <Typography variant="caption" display="block">
                  Placas: {data?.placa}
                </Typography>
                <Typography variant="caption" display="block" noWrap>
                  Origem: {data?.origemvViagem}
                </Typography>
                <Typography variant="caption" display="block" noWrap>
                  Destino: {data?.destinoViagem}
                </Typography>
                <Typography variant="caption" display="block" noWrap>
                  Obs: {data?.obs}
                </Typography>
              </Box>
            </Paper>
          </Grid>
        ))}
    </Grid>
  )
}

const GSVTechConsultando = ({ handleRefresh, loading }) => {
  useEffect(() => {
    const interval = setInterval(async () => {
      handleRefresh()
    }, 15000)

    return () => {
      clearInterval(interval)
    }
  }, [])

  return (
    <Tooltip title="Integração com a GSVTech em progresso">
      <Grid container spacing={1} alignItems="center">
        <Grid item>
          <Info color="secondary" />
        </Grid>
        <Grid item>{loading && <CircularProgress size={12} />}</Grid>
      </Grid>
    </Tooltip>
  )
}

const GSVTechErro = ({ handleRecreate, loading }) => {
  return (
    <Grid container spacing={4} alignItems="center">
      <Grid item>
        <Tooltip title="Erro na integração com a GSVTech">
          <Cancel color="secondary" />
        </Tooltip>
      </Grid>
      <Grid item>
        <Button
          variant="outlined"
          onClick={handleRecreate}
          disabled={loading}
          color="primary"
        >
          Tentar novamente
        </Button>
      </Grid>
    </Grid>
  )
}

export default CardGSVTech
